import type { JSX } from 'react';

import { Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';
import type { QrCodeType } from '@change-corgi/middle-end-gql-schema';

import { PetitionQRCode } from './components/PetitionQRCode';
import { PetitionQRCodeDownloadLink } from './components/PetitionQRCodeDownloadLink';

/**
 * Note: There is a duplicate of this component at:
 * apps/front-end/src/app/pages/starterDashboardV2/shared/components/qrcode/PetitionQRCodeDownload.tsx
 *
 * That version does not use legacy design system components,
 * and has a layout tailored to the petition dashboard's needs.
 * When making changes here, consider if they should also be applied there, and vice versa.
 *
 * Props:
 * @param {string} slugOrId              The petition slug or ID to generate the QR code for.
 * @param {number} displaySize           Display size in pixels—used to reserve space for the QR image
 *                                       before load and prevent cumulative layout shift.
 * @param {QrCodeType} displayType       Type for the displayed QR code.
 * @param {QrCodeType} downloadType      Type for the downloadable QR code.
 * @param {number} [downloadSize]        Optional size in pixels for the downloadable QR code.
 *                                       If omitted, will use default size configured in GraphQL.
 * @param {string} descriptionText       Text shown beside the QR code explaining its purpose.
 * @param {string} downloadLinkText      Text for the download link.
 * @param {string} [downloadFilename]    Optional filename for the QR code download (defaults to 'petition-qrcode.png').
 * @param {() => Promise<void>} [onDownload] Optional handler called after download is initiated.
 */

export function PetitionQRCodeDownload({
	slugOrId,
	displaySize,
	displayType,
	downloadSize,
	downloadType,
	descriptionText,
	downloadLinkText,
	downloadFilename,
	onDownload,
}: {
	slugOrId: string;
	displaySize: number;
	displayType: QrCodeType;
	downloadType: QrCodeType;
	downloadSize?: number;
	descriptionText: string;
	downloadLinkText: string;
	downloadFilename?: string;
	onDownload?: () => Promise<void>;
}): JSX.Element {
	return (
		<Flex sx={{ gap: '16' }}>
			<div sx={{ flexShrink: 0 }}>
				<PetitionQRCode slugOrId={slugOrId} size={displaySize} type={displayType} />
			</div>
			<Flex sx={{ flexDirection: 'column', gap: 8 }}>
				<Text>{descriptionText}</Text>
				<PetitionQRCodeDownloadLink
					slugOrId={slugOrId}
					size={downloadSize}
					type={downloadType}
					onDownload={onDownload}
					filename={downloadFilename}
				>
					{downloadLinkText}
				</PetitionQRCodeDownloadLink>
			</Flex>
		</Flex>
	);
}
